import { v4 as uuidv4 } from 'uuid';
import { useCallback, useState, useRef } from 'react';
import { difference } from 'lodash';
import {
  addItem,
  updateItem,
  removeItem,
  updateItemImage,
  closeModals,
} from 'data/cacheHelpers/menu';
import useBrands from 'data/hooks/useBrands';
import useMenu from 'data/hooks/useMenuActions';
import { ModalItem } from 'model/menu';
import { CDError } from 'utils/error';

const useConnect = (
  previousData: ModalItem,
  brandId: string,
  menuId: string,
) => {
  const [tab, setTab] = useState<'details' | 'modifiers'>('details');
  const newItemId = useRef<string>(uuidv4());
  const { uploadImage, uploadImageLoading } = useMenu();

  const [isConfirmRemoveOpen, setConfirmRemove] = useState(false);

  const {
    itemId,
    categoryId: previousCategoryId,
    childrenEntities: previousChildrenEntities,
  } = previousData;
  const isNew = itemId === undefined;

  const allowEditImage =
    useBrands()
      .brands.find((brand) => brand.id === brandId)
      ?.features.includes('OM_WEB_MENU_ITEM_IMAGE_EDITION') || false;

  const handleSubmit = useCallback(
    (values: any) => {
      if (isNew) {
        addItem(brandId, values, newItemId.current);
      } else {
        const previousModifiersId = previousChildrenEntities.map(
          (entity) => entity.id,
        );
        const updatedModifiersId = values.childrenEntities.map(
          (entity: any) => entity.id,
        );
        const deletedItemModifiers = difference(
          previousModifiersId,
          updatedModifiersId,
        );

        updateItem(brandId, values, previousCategoryId, deletedItemModifiers);
      }
      closeModals(brandId);
    },
    [isNew, brandId, previousCategoryId, previousChildrenEntities],
  );

  const handleUploadImage = useCallback(
    async (image: File, setFieldValue: (field: string, value: any) => void) => {
      const result = await uploadImage({
        menuId,
        itemId: itemId || newItemId.current,
        image,
      });

      if (result instanceof CDError) {
        return null;
      }

      setFieldValue('imageUrl', result.original);
      setFieldValue('imageUrlSmall', result.small);
      setFieldValue('imageUrlMedium', result.medium);

      if (isNew) {
        return null;
      }
      updateItemImage(
        brandId,
        itemId,
        result.original,
        result.small,
        result.medium,
      );

      return null;
    },
    [itemId, newItemId, uploadImage, isNew, brandId, menuId],
  );

  const closeModal = useCallback(() => closeModals(brandId), [brandId]);

  const handleRemoveItem = useCallback(() => {
    removeItem(brandId, itemId, previousCategoryId);
    closeModals(brandId);
  }, [brandId, itemId, previousCategoryId]);

  const askConfirmRemove = useCallback(() => setConfirmRemove(true), []);
  const closeConfirmRemove = useCallback(() => setConfirmRemove(false), []);

  const setDetailsTab = useCallback(() => setTab('details'), []);
  const setModifierTab = useCallback(() => setTab('modifiers'), []);

  return {
    tab,
    setTab,
    isConfirmRemoveOpen,
    setConfirmRemove,
    itemId,
    previousCategoryId,
    previousChildrenEntities,
    isNew,
    allowEditImage,
    handleSubmit,
    closeModal,
    handleRemoveItem,
    askConfirmRemove,
    closeConfirmRemove,
    setDetailsTab,
    setModifierTab,
    handleUploadImage,
    uploadImageLoading,
  };
};

export default useConnect;
